import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button, Card, Checkbox, Cluster, FormField, Input, PasswordInput, Select, Stack, Tabs, Textarea } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { api } from "./api";
import { Flash } from "./Flash";
import { formatAiUsd, formatUtc } from "./format";
import { notifySaved } from "./savedToast";

interface PlatformAi {
  endpoint: string;
  model: string;
  key_last4: string | null;
  key_set: boolean;
  input_cost_per_m: number;
  output_cost_per_m: number;
  grounding_prompt: string;
  grounding_prompt_is_default: boolean;
  default_grounding_prompt: string;
  enabled: boolean;
  default_monthly_budget_cents: number;
  automatic_pricing?: string | null;
}

interface UsageRow {
  id?: string;
  org_id: string | null;
  org_name: string | null;
  product?: string;
  source: "platform" | "byok";
  input_tokens: number | string | null;
  output_tokens: number | string | null;
  cost_usd: number | string | null;
  created_at?: string | null;
  ok?: boolean;
}

interface UsageTotals {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

interface AreaLimit {
  key: string;
  label: string;
  help: string;
  default: number;
  min: number;
  max: number;
  value: number;
}

/** One AI area (Lead generator, ...). The server's registry decides which exist; each gets its own tab. */
interface AiArea {
  key: string;
  label: string;
  description: string;
  default_prompt: string;
  prompt: string;
  prompt_is_default: boolean;
  limits: AreaLimit[];
}

interface TestResult {
  ok: boolean;
  reply?: string;
  model?: string;
  error?: string;
  tested_at?: string;
}

function testedStamp(iso: string | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())} UTC`;
}

export function AiPage() {
  const [tab, setTab] = useState("general");
  const [areas, setAreas] = useState<AiArea[]>([]);
  const [areasError, setAreasError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ areas: AiArea[] }>("/operator/ai/areas")
      .then((data) => setAreas(data.areas))
      .catch((err) => setAreasError(err instanceof Error ? err.message : "AI areas could not be loaded."));
  }, []);

  const tabs = [
    { key: "general", label: "General" },
    ...areas.map((area) => ({ key: `area:${area.key}`, label: area.label })),
    { key: "usage", label: "Usage" },
  ];
  const activeArea = areas.find((area) => `area:${area.key}` === tab);

  return (
    <Stack gap={4}>
      <TipHeading
        as="h2"
        label="AI"
        tip="The platform AI connection every organization uses unless it has configured its own in Organization Settings. Each AI area of the app has its own tab with its prompt and limits."
      />
      <p className="admin-muted">
        Pricing an AI add-on, or sizing a customer&apos;s cost limits? Read{" "}
        <Link to="/ai/pricing-guide">How AI pricing works</Link>.
      </p>
      <Tabs items={tabs} activeKey={tab} onChange={setTab} />
      {areasError && <Flash tone="error">{areasError}</Flash>}
      {tab === "general" && <McpSwitchCard />}
      {tab === "general" && <GeneralTab />}
      {activeArea && (
        <AreaTab
          key={activeArea.key}
          area={activeArea}
          onSaved={(saved) => setAreas((current) => current.map((item) => (item.key === saved.key ? saved : item)))}
        />
      )}
      {activeArea?.key === "loxley" && <LoxleyFeedbackTable />}
      {tab === "usage" && <UsageTab />}
    </Stack>
  );
}

interface LoxleyFeedbackRow {
  id: string;
  org_id: string;
  org_name: string | null;
  user_email: string | null;
  created_at: string;
  feedback_at: string | null;
  message: string;
  reply: string | null;
  feedback: "up" | "down";
  feedback_reason: string | null;
  feedback_comment: string | null;
}

const FEEDBACK_REASON_LABELS: Record<string, string> = {
  wrong: "Answer was wrong",
  wrong_record: "Wrong record",
  incomplete: "Incomplete",
  not_helpful: "Didn't help",
  too_slow: "Too slow",
  unsafe: "Unsafe suggestion",
  other: "Something else",
};

/** Every customer's thumbs up/down on Loxley answers, newest first. The customer page has the full turn log. */
function LoxleyFeedbackTable() {
  const [days, setDays] = useState("30");
  const [rating, setRating] = useState<"" | "up" | "down">("");
  const [rows, setRows] = useState<LoxleyFeedbackRow[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRows(null);
    setError(null);
    const query = `days=${days}${rating ? `&feedback=${rating}` : ""}`;
    api
      .get<{ rows?: LoxleyFeedbackRow[]; truncated?: boolean }>(`/operator/ai/loxley/feedback?${query}`)
      .then((data) => {
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTruncated(Boolean(data?.truncated));
      })
      .catch((err) => {
        setRows([]);
        setError(err instanceof Error ? err.message : "Loxley feedback could not be loaded.");
      });
  }, [days, rating]);

  const up = rows?.filter((row) => row.feedback === "up").length ?? 0;
  const down = rows?.filter((row) => row.feedback === "down").length ?? 0;

  return (
    <Card>
      <Stack gap={3}>
        <TipHeading
          as="h3"
          label="Loxley feedback"
          tip="Thumbs up and down from every customer, with the reason and comment the user gave. Open the customer's Loxley card for the full turn log."
        />
        <Cluster gap={3} align="end">
          <FormField label="Period" htmlFor="loxley-feedback-period">
            <Select id="loxley-feedback-period" value={days} onChange={(event) => setDays(event.target.value)}>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 12 months</option>
            </Select>
          </FormField>
          <FormField label="Rating" htmlFor="loxley-feedback-rating">
            <Select id="loxley-feedback-rating" value={rating}
              onChange={(event) => setRating(event.target.value as "" | "up" | "down")}>
              <option value="">Up and down</option>
              <option value="down">Thumbs down</option>
              <option value="up">Thumbs up</option>
            </Select>
          </FormField>
          {rows && rows.length ? <p className="admin-muted">{up} up, {down} down</p> : null}
        </Cluster>
        {error && <Flash tone="error">{error}</Flash>}
        {rows === null && !error ? (
          <p className="admin-muted">Loading…</p>
        ) : rows && rows.length === 0 && !error ? (
          <p className="admin-muted">No Loxley feedback in this period.</p>
        ) : rows && rows.length > 0 ? (
          <div className="admin-table-scroll">
            {truncated && <p className="admin-muted">Showing the latest 200 ratings.</p>}
            <table>
              <thead>
                <tr>
                  <th>Rated</th>
                  <th>Customer</th>
                  <th>User</th>
                  <th>Rating</th>
                  <th>Reason</th>
                  <th>Comment</th>
                  <th>Question</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatUtc(row.feedback_at ?? row.created_at)}</td>
                    <td>{row.org_name ?? row.org_id}</td>
                    <td>{row.user_email ?? "—"}</td>
                    <td>{row.feedback === "up" ? "Thumbs up" : "Thumbs down"}</td>
                    <td>{row.feedback_reason ? FEEDBACK_REASON_LABELS[row.feedback_reason] ?? row.feedback_reason : "—"}</td>
                    <td>{row.feedback_comment || "—"}</td>
                    <td title={row.reply ?? undefined}>{row.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Stack>
    </Card>
  );
}

function AreaTab({ area, onSaved }: { area: AiArea; onSaved: (area: AiArea) => void }) {
  const [prompt, setPrompt] = useState(area.prompt);
  const [limits, setLimits] = useState<Record<string, string>>(
    Object.fromEntries(area.limits.map((limit) => [limit.key, String(limit.value)])),
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await api.put<AiArea>(`/operator/ai/areas/${area.key}`, {
        prompt,
        limits: Object.fromEntries(area.limits.map((limit) => [limit.key, Number(limits[limit.key])])),
      });
      onSaved(saved);
      setPrompt(saved.prompt);
      setNotice(`${area.label} AI settings saved.`);
      notifySaved(`${area.label} AI settings saved.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Settings could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <Stack gap={3}>
        <p className="admin-muted">{area.description}</p>
        {error && <Flash tone="error">{error}</Flash>}
        {notice && <Flash tone="success">{notice}</Flash>}
        <FormField
          label={`${area.label} prompt`}
          htmlFor={`ai-area-${area.key}-prompt`}
          tooltip="Added to every AI call this area makes, after the default grounding prompt. Applies to all customers."
        >
          <Textarea
            id={`ai-area-${area.key}-prompt`}
            rows={6}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
          />
        </FormField>
        <Cluster gap={3} align="center">
          <Button variant="secondary" onClick={() => setPrompt(area.default_prompt)} disabled={prompt === area.default_prompt}>
            Reset to default
          </Button>
          <p className="admin-muted">{prompt === area.default_prompt ? "Using the default prompt." : "Custom prompt."}</p>
        </Cluster>
        {area.limits.length > 0 ? (
          <>
        <TipHeading
          label={`${area.label} limits`}
          tip={
            area.key === "leadgen"
              ? "Platform defaults. Search radius, fit score, and the read limits can be replaced "
                + "by a customer in Organization settings, AI, Lead gen, when Lead generator is enabled for them. "
                + "Default leads per month is only the starting allowance; the live monthly limit is on the customer page."
              : "Applied to every customer. Values outside the allowed range are refused."
          }
        />
        {area.limits.map((limit) => (
          <FormField
            key={limit.key}
            label={`${limit.label} (${limit.min}-${limit.max}, default ${limit.default})`}
            htmlFor={`ai-area-${area.key}-${limit.key}`}
            tooltip={limit.help}
          >
            <Input
              id={`ai-area-${area.key}-${limit.key}`}
              type="number"
              min={limit.min}
              max={limit.max}
              value={limits[limit.key] ?? ""}
              onChange={(event) => setLimits({ ...limits, [limit.key]: event.target.value })}
            />
          </FormField>
        ))}
          </>
        ) : null}
        <Cluster gap={3}>
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? "Saving…" : `Save ${area.label.toLowerCase()} settings`}
          </Button>
        </Cluster>
      </Stack>
    </Card>
  );
}

function GeneralTab() {
  const [ai, setAi] = useState<PlatformAi | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [budget, setBudget] = useState("");
  const [inCost, setInCost] = useState("");
  const [outCost, setOutCost] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<TestResult | null>(null);

  function apply(row: PlatformAi) {
    setAi(row);
    setApiKey("");
    setBudget(String((row.default_monthly_budget_cents || 0) / 100));
    setInCost(String(row.input_cost_per_m ?? 0));
    setOutCost(String(row.output_cost_per_m ?? 0));
  }

  useEffect(() => {
    api
      .get<PlatformAi>("/operator/ai")
      .then(apply)
      .catch((err) => setError(err instanceof Error ? err.message : "AI settings could not be loaded."));
  }, []);

  async function save() {
    if (!ai) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      apply(
        await api.put<PlatformAi>("/operator/ai", {
          endpoint: ai.endpoint,
          api_key: apiKey || null,
          model: ai.model,
          input_cost_per_m: Number(inCost) || 0,
          output_cost_per_m: Number(outCost) || 0,
          grounding_prompt: ai.grounding_prompt,
          enabled: ai.enabled,
          default_monthly_budget_cents: Math.round(Number(budget || 0) * 100),
        }),
      );
      setNotice("AI settings saved.");
      notifySaved("AI settings saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "AI settings could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  async function runTest() {
    setTesting(true);
    setTest(null);
    try {
      setTest(await api.post<TestResult>("/operator/ai/test", {}));
    } catch (err) {
      setTest({ ok: false, error: err instanceof Error ? err.message : "The test could not run." });
    } finally {
      setTesting(false);
    }
  }

  if (!ai) return error ? <Flash tone="error">{error}</Flash> : <p className="admin-muted">Loading…</p>;

  return (
    <Card>
      <Stack gap={3}>
        <p className="admin-muted">
          The platform-default AI connection is used by every organization that has not configured its own &ldquo;bring
          your own AI&rdquo; connection in Organization Settings. Cost rates here are admin-console-only and are never
          visible to customers.
        </p>
        {error && <Flash tone="error">{error}</Flash>}
        {notice && <Flash tone="success">{notice}</Flash>}
        <FormField label="API endpoint" htmlFor="ai-endpoint" tooltip="OpenAI-compatible base address, e.g. https://api.deepseek.com.">
          <Input id="ai-endpoint" value={ai.endpoint} onChange={(event) => setAi({ ...ai, endpoint: event.target.value })} />
        </FormField>
        <FormField label={ai.key_set ? `API key (currently ----${ai.key_last4})` : "API key (not set)"} htmlFor="platform-ai-key">
          <PasswordInput
            id="platform-ai-key"
            autoComplete="off"
            placeholder={ai.key_set ? "Leave blank to keep the current key" : "Paste the API key"}
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
          />
        </FormField>
        <FormField label="Model name" htmlFor="ai-model" tooltip="Sent as the model on every call, e.g. deepseek-chat.">
          <Input id="ai-model" value={ai.model} onChange={(event) => setAi({ ...ai, model: event.target.value })} />
        </FormField>
        <FormField label="Input cost per 1M tokens (USD)" htmlFor="ai-input-cost" tooltip="Leave at 0 to use the published price for the model above, including cache hit and miss. A number here bills every input token at this rate.">
          <Input id="ai-input-cost"
            type="number"
            min={0}
            step="0.01"
            value={inCost}
            onChange={(event) => setInCost(event.target.value)}
          />
        </FormField>
        <FormField label="Output cost per 1M tokens (USD)" htmlFor="ai-output-cost" tooltip="Leave at 0 to use the published price for the model above. A number here replaces that price.">
          <Input id="ai-output-cost"
            type="number"
            min={0}
            step="0.01"
            value={outCost}
            onChange={(event) => setOutCost(event.target.value)}
          />
        </FormField>
        {ai.automatic_pricing && Number(inCost) === 0 && Number(outCost) === 0 && (
          <p className="admin-muted">{ai.automatic_pricing}</p>
        )}
        <FormField
          label="Default grounding prompt (all AI areas)" htmlFor="ai-grounding"
          tooltip="Prepended to every AI call in every area for every organization: tone, rules, things never to do. Pre-filled with the Saleslox default."
        >
          <Textarea id="ai-grounding" rows={5} value={ai.grounding_prompt} onChange={(event) => setAi({ ...ai, grounding_prompt: event.target.value })} />
        </FormField>
        <Cluster gap={3} align="center">
          <Button
            variant="secondary"
            onClick={() => setAi({ ...ai, grounding_prompt: ai.default_grounding_prompt })}
            disabled={ai.grounding_prompt === ai.default_grounding_prompt}
          >
            Reset grounding prompt to default
          </Button>
          <p className="admin-muted">Area prompts, such as Lead generator, are on their own tabs above.</p>
        </Cluster>
        <FormField label="Default monthly budget for new organizations (USD)" htmlFor="ai-default-budget" tooltip="Starting AI spend cap per organization.">
          <Input id="ai-default-budget" value={budget} onChange={(event) => setBudget(event.target.value)} />
        </FormField>
        <Checkbox
          label="AI enabled for new organizations"
          checked={ai.enabled}
          onChange={(event) => setAi({ ...ai, enabled: event.target.checked })}
        />
        <Cluster gap={3}>
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? "Saving…" : "Save AI settings"}
          </Button>
          <Button variant="secondary" onClick={() => void runTest()} disabled={testing}>
            {testing ? "Testing…" : "Test connection"}
          </Button>
        </Cluster>
        <p className="admin-muted">Sends &ldquo;Hello world&rdquo; through the currently-saved connection above (save first if you just changed it).</p>
        {test && (test.ok ? (
          <Flash tone="success">{`Connected (${test.model}) at ${testedStamp(test.tested_at)}: ${test.reply}`}</Flash>
        ) : <Flash tone="error">{test.error ?? "Failed."}</Flash>)}
      </Stack>
    </Card>
  );
}

/**
 * Platform-wide switch for external AI apps (Loxley's MCP server and its OAuth sign-in).
 * Off = every organization's Claude/ChatGPT/MCP connections are refused at once.
 * The in-app Loxley assistant does not depend on it.
 */
function McpSwitchCard() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<{ enabled: boolean }>("/operator/ai/mcp")
      .then((row) => setEnabled(row.enabled))
      .catch((err) => setError(err instanceof Error ? err.message : "The MCP setting could not be loaded."));
  }, []);

  async function save() {
    if (enabled === null) return;
    setSaving(true);
    setError(null);
    try {
      const row = await api.put<{ enabled: boolean }>("/operator/ai/mcp", { enabled });
      setEnabled(row.enabled);
      notifySaved(row.enabled ? "MCP enabled." : "MCP turned off.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "The MCP setting could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <Stack gap={3}>
        <TipHeading
          label="External AI apps (MCP)"
          tip={
            "Lets customers with the Loxley add-on connect Claude, ChatGPT and other MCP apps to Saleslox. "
            + "Save turns it off for every connection on the platform; keys are kept and work again "
            + "when it is turned back on. The in-app Loxley assistant is not affected."
          }
        />
        {error && <Flash tone="error">{error}</Flash>}
        <Checkbox
          label="MCP enabled for the whole platform"
          checked={Boolean(enabled)}
          disabled={enabled === null || saving}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        <Cluster gap={3}>
          <Button onClick={() => void save()} disabled={enabled === null || saving}>
            {saving ? "Saving…" : "Save MCP setting"}
          </Button>
        </Cluster>
      </Stack>
    </Card>
  );
}

function tokenCount(value: unknown): string {
  const count = Number(value);
  return Number.isFinite(count) ? count.toLocaleString("en-US") : "0";
}

function UsageTab() {
  const [days, setDays] = useState("30");
  const [rows, setRows] = useState<UsageRow[] | null>(null);
  const [totals, setTotals] = useState<UsageTotals | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRows(null);
    setTotals(null);
    setError(null);
    api
      .get<{ rows?: UsageRow[]; totals?: UsageTotals; truncated?: boolean }>(`/operator/ai/usage?days=${days}`)
      .then((data) => {
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotals(data?.totals ?? null);
        setTruncated(Boolean(data?.truncated));
      })
      .catch((err) => {
        setRows([]);
        setError(err instanceof Error ? err.message : "Usage could not be loaded.");
      });
  }, [days]);

  const platformSpend = totals?.cost_usd ?? (rows ?? [])
    .filter((row) => row.source === "platform")
    .reduce((sum, row) => sum + (Number(row.cost_usd) || 0), 0);

  return (
    <Card>
      <Stack gap={3}>
        <Cluster gap={3} align="end">
          <FormField label="Period" htmlFor="ai-usage-period">
            <Select id="ai-usage-period" value={days} onChange={(event) => setDays(event.target.value)}>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 12 months</option>
            </Select>
          </FormField>
          <p className="admin-muted">Platform spend in period: {formatAiUsd(platformSpend)}</p>
        </Cluster>
        {error && <Flash tone="error">{error}</Flash>}
        {rows === null && !error ? (
          <p className="admin-muted">Loading…</p>
        ) : rows && rows.length === 0 && !error ? (
          <p className="admin-muted">No AI calls in this period.</p>
        ) : rows && rows.length > 0 ? (
          <div className="admin-table-scroll">
            {truncated && <p className="admin-muted">Showing the latest 500 calls. Totals cover the whole period.</p>}
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Input tokens</th>
                  <th>Output tokens</th>
                  <th>Cost (USD)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id ?? `${row.org_id ?? "platform"}-${index}`}>
                    <td>{row.created_at ? formatUtc(row.created_at) : "—"}</td>
                    <td>{row.org_name ?? row.org_id ?? "Admin console tests"}</td>
                    <td>{row.product || "AI"}{row.ok === false ? " (failed)" : ""}</td>
                    <td>{tokenCount(row.input_tokens)}</td>
                    <td>{tokenCount(row.output_tokens)}</td>
                    <td>{row.source === "byok" ? "—" : formatAiUsd(Number(row.cost_usd))}</td>
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td>{tokenCount(totals.calls)} calls</td>
                    <td />
                    <td>{tokenCount(totals.input_tokens)}</td>
                    <td>{tokenCount(totals.output_tokens)}</td>
                    <td>{formatAiUsd(totals.cost_usd)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : null}
      </Stack>
    </Card>
  );
}
